import React, { useEffect, useRef, useState } from 'react';
import UiIcon from '../ui/UiIcon';
import EchoSignalGame from './EchoSignalGame';
import { resolveCommand } from './terminalCommands';

const PROMPT = 'ty0x7@desktop:~$';

const ConnectionsTerminal = ({ apps, destinations = [], onOpen, onClose, active = true }) => {
  const [command, setCommand] = useState('');
  const [selection, setSelection] = useState(0);
  const [history, setHistory] = useState([]);
  const [showGreeting, setShowGreeting] = useState(true);
  const [gameOpen, setGameOpen] = useState(false);
  const [gameOutput, setGameOutput] = useState('');
  const inputRef = useRef(null);
  const mirrorRef = useRef(null);
  const logRef = useRef(null);
  const gameRef = useRef(null);
  const commandsRef = useRef([]);
  const historyPositionRef = useRef(0);
  const draftRef = useRef('');
  const allDestinations = [...destinations, ...apps];

  const syncCaret = () => {
    const input = inputRef.current;
    if (!input) return;
    setSelection(input.selectionStart ?? input.value.length);
    if (mirrorRef.current) mirrorRef.current.scrollLeft = input.scrollLeft;
  };
  const focusPrompt = () => inputRef.current?.focus({ preventScroll: true });
  useEffect(() => {
    if (!active || gameOpen || !window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const frame = window.requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    return () => window.cancelAnimationFrame(frame);
  }, [active, gameOpen]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const viewport = logRef.current;
      if (viewport) viewport.scrollTop = gameOpen ? 0 : viewport.scrollHeight;
      syncCaret();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [history, gameOpen, command]);

  const clear = () => { setHistory([]); setShowGreeting(false); setGameOutput(''); };
  const exitGame = () => { setGameOpen(false); window.requestAnimationFrame(focusPrompt); };
  const append = (input, response) => setHistory(current => [...current.slice(-199), { input, response }]);
  const execute = event => {
    event.preventDefault();
    const input = command.trim();
    if (!input) return;
    commandsRef.current.push(input);
    historyPositionRef.current = commandsRef.current.length;
    draftRef.current = '';
    setCommand(''); setSelection(0);
    const result = resolveCommand(input, { destinations: allDestinations, history: commandsRef.current, gameOpen });
    if (result.action === 'clear') { clear(); if (gameOpen) exitGame(); return; }
    if (result.action === 'game') setGameOpen(true);
    if (result.action === 'exit') exitGame();
    if (result.action === 'close') onClose?.();
    if (result.action === 'external') window.open(result.destination.href, '_blank', 'noopener,noreferrer');
    if (result.action === 'open') onOpen?.(result.destination.id);
    const response = result.action === 'game-control' ? gameRef.current?.execute(result.control) : result.text;
    setGameOutput(result.action === 'game' || (result.action === 'game-control' && result.control !== 'score' && !response?.startsWith('No active')) ? '' : response || '');
    append(input, response || '');
  };

  const onKeyDown = event => {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'l') { event.preventDefault(); clear(); return; }
    if (event.ctrlKey && event.key.toLowerCase() === 'c') {
      event.preventDefault(); append(`${command}^C`, ''); setCommand(''); setSelection(0);
      if (gameOpen) exitGame();
      return;
    }
    if (event.key === 'Tab') {
      if (event.shiftKey || !command) return;
      event.preventDefault();
      const match = command.match(/^(open|launch|cat|repo)\s+(.*)$/i);
      const choices = match ? allDestinations.map(item => item.label.toLowerCase()).filter(name => name.startsWith(match[2].toLowerCase()))
        : ['help', 'ls', 'apps', 'links', 'open', 'cat', 'repo', 'whoami', 'about', 'pwd', 'date', 'echo', 'history', 'clear', 'games', 'game', 'start', 'pause', 'resume', 'restart', 'score', 'exit'].filter(name => name.startsWith(command));
      if (choices.length === 1) setCommand(`${match ? `${match[1]} ` : ''}${choices[0]}${match ? '' : ' '}`);
      else if (choices.length > 1) append(command, choices.join('  '));
      return;
    }
    if (!['ArrowUp', 'ArrowDown'].includes(event.key)) return;
    event.preventDefault();
    if (historyPositionRef.current === commandsRef.current.length) draftRef.current = command;
    const next = Math.max(0, Math.min(commandsRef.current.length, historyPositionRef.current + (event.key === 'ArrowUp' ? -1 : 1)));
    historyPositionRef.current = next;
    setCommand(next === commandsRef.current.length ? draftRef.current : commandsRef.current[next]);
  };

  return <div className={`connections-terminal${gameOpen ? ' is-game-open' : ''}`}>
    <div ref={logRef} className="terminal-viewport" role="region" aria-label="Scrollable terminal" onClick={event => {
      if (!gameOpen && !event.target.closest('input, button, a') && !window.getSelection()?.toString()) focusPrompt();
    }}>
      {!gameOpen && showGreeting && <pre className="terminal-greeting">{'TY0X7 shell\n\n'}</pre>}
      {!gameOpen && <div className="terminal-log" role="log" aria-label="Command output">{history.map((entry, index) => <div key={index}><p><span>{PROMPT}</span> {entry.input}</p><pre className="terminal-response">{entry.response}</pre></div>)}</div>}
      {gameOpen && <EchoSignalGame ref={gameRef} active={active} onExit={exitGame} />
      }
      {gameOpen && <button className="terminal-game-exit" type="button" onClick={exitGame}>← Return to terminal</button>}
      {gameOpen && <pre className="terminal-response terminal-game-output" role="log" aria-label="Command output">{gameOutput}</pre>}
      <form className="terminal-command" onSubmit={execute}>
        <label htmlFor="connection-command">{PROMPT}</label>
        <div className="terminal-input-wrap">
          <div ref={mirrorRef} className="terminal-input-mirror" aria-hidden="true"><span>{command.slice(0, selection)}</span><span className="connection-caret">|</span><span>{command.slice(selection)}</span></div>
          <input ref={inputRef} id="connection-command" aria-label="Terminal command" value={command} onChange={event => { setCommand(event.target.value); setSelection(event.target.selectionStart); }} onSelect={syncCaret} onScroll={syncCaret} onKeyDown={onKeyDown} autoComplete="off" autoCapitalize="off" spellCheck={false} />
        </div>
        <button className="terminal-touch-only" type="submit" aria-label="Run command"><UiIcon name="enter" /></button>
      </form>
    </div>
  </div>;
};

export default ConnectionsTerminal;
